import {
	Badge,
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@dune/ui";
import { WEBHOOK_EVENT_DETAILS, type WebhookDto } from "@dune/types";
import { PencilLine, PlusCircle } from "lucide-react";
import { formatDateTime } from "@dune/utils";

export interface OverviewTabProps {
	isLoading: boolean;
	error: unknown;
	webhooks: WebhookDto[];
	onCreate: () => void;
	onEdit: (webhook: WebhookDto) => void;
}

export const OverviewTab = ({
	isLoading,
	error,
	webhooks,
	onCreate,
	onEdit,
}: OverviewTabProps) => (
	<div className="space-y-6">
		<Card>
			<CardHeader>
				<CardTitle>Webhooks disponibles</CardTitle>
			</CardHeader>
			<CardContent>
				{isLoading ? (
					<div className="space-y-3">
						{Array.from({ length: 6 }).map((index) => (
							<Skeleton
								key={`webhook-skeleton-${index}`}
								className="h-12 w-full"
							/>
						))}
					</div>
				) : error ? (
					<div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-destructive">
						Une erreur est survenue lors du chargement des webhooks.
					</div>
				) : webhooks.length === 0 ? (
					<div className="flex flex-col items-center justify-center gap-4 rounded-md border border-dashed p-8 text-center">
						<p className="text-muted-foreground">
							Aucun webhook configure pour le moment.
						</p>
						<Button onClick={onCreate}>
							<PlusCircle className="mr-2 h-4 w-4" />
							Configurer un webhook
						</Button>
					</div>
				) : (
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Nom</TableHead>
								<TableHead>URL</TableHead>
								<TableHead>Évènements</TableHead>
								<TableHead>Statut</TableHead>
								<TableHead>Derniere livraison</TableHead>
								<TableHead className="w-[80px] text-right">Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{webhooks.map((webhook) => (
								<TableRow key={webhook.id}>
									<TableCell>
										<div className="font-medium">{webhook.name}</div>
										{webhook.description && (
											<p className="text-xs text-muted-foreground">
												{webhook.description}
											</p>
										)}
									</TableCell>
									<TableCell>
										<a
											href={webhook.url}
											target="_blank"
											rel="noreferrer"
											className="text-sm text-primary underline break-all"
										>
											{webhook.url}
										</a>
									</TableCell>
									<TableCell>
										<div className="flex flex-wrap gap-1">
											{webhook.events.map((event) => (
												<Badge key={`${webhook.id}-${event}`} variant="outline">
													{WEBHOOK_EVENT_DETAILS[event]?.label ?? event}
												</Badge>
											))}
										</div>
									</TableCell>
									<TableCell>
										<Badge variant={webhook.isActive ? "default" : "outline"}>
											{webhook.isActive ? "Actif" : "Suspendu"}
										</Badge>
									</TableCell>
									<TableCell>
										{webhook.lastDeliveryAt
											? formatDateTime(webhook.lastDeliveryAt)
											: "—"}
									</TableCell>
									<TableCell className="text-right">
										<Button
											variant="ghost"
											size="icon"
											onClick={() => onEdit(webhook)}
											aria-label={`Modifier ${webhook.name}`}
										>
											<PencilLine className="h-4 w-4" />
										</Button>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</CardContent>
		</Card>
	</div>
);
